/**
 * Scene-editor scrub/gesture writes: live iframe preview immediately,
 * debounced setSelectedVersionPathValue (clone / React / analytics / DB).
 * Does not touch PlayEngine or conventional TreeView editors.
 */

import _ from "lodash";
import { pushPreviewPatch, buildCommitPreviewPatch } from "./previewPatch";

const PERSIST_DEBOUNCE_MS = 150;
const PREVIEW_IFRAME_ID = "gamePreviewIframe";

let workingGameConfig = null;
let workingVersionId = null;

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const persistTimers = new Map();
/** @type {Map<string, { path: string, value: *, setValue: Function }>} */
const persistLatest = new Map();

/**
 * Live-preview only (no React version update). Skips PlayEngine bases.
 * Omits home.applyPreview's console.dir — that was a major scrub cost.
 */
export function sceneEditorApplyLivePreview(gameConfig, selectedBase) {
  if (!selectedBase || _.get(selectedBase, "playEngineInstance", false)) return;
  const iframe = document.getElementById(PREVIEW_IFRAME_ID);
  if (!iframe || !_.isFunction(_.get(iframe, "contentWindow.applyGameConfig"))) return;
  try {
    iframe.contentWindow.applyGameConfig(_.cloneDeep(gameConfig));
  } catch (err) {
    console.trace(err);
  }
}

/**
 * Apply one path onto a working gameConfig clone and push to the playable.
 * `path` / `value` must already match what setSelectedVersionPathValue would store
 * (including localizationOverrides rewrite when applicable).
 *
 * Scrub ticks accumulate in the working clone, but only the touched top-level
 * property is shipped to the playable: a full applyGameConfig per tick costs a
 * cloneDeep of the whole config on both sides. Paths with no safe partial
 * (gameConfig.options.*, …) still push the working clone.
 */
export function sceneEditorGesturePreviewSet({
  selectedVersion,
  selectedBase,
  path,
  value,
}) {
  // Ecomm bases carry playEngineInstance too, so this skips both of them.
  if (!selectedBase || _.get(selectedBase, "playEngineInstance", false)) return;

  const versionId = String(_.get(selectedVersion, "_id", ""));
  if (!workingGameConfig || workingVersionId !== versionId) {
    workingGameConfig = _.cloneDeep(_.get(selectedVersion, "gameConfig") || {});
    workingVersionId = versionId;
  }
  const isPrefixed = typeof path === "string" && path.startsWith("gameConfig.");
  const gcPath = isPrefixed ? path.slice("gameConfig.".length) : path;
  _.set(workingGameConfig, gcPath, _.cloneDeep(value));

  // Built off the working clone rather than selectedVersion: the persist is
  // debounced, so the version still lacks the earlier ticks of this gesture and
  // a patch built from it would push the playable back to a stale value.
  const patch = buildCommitPreviewPatch(
    { gameConfig: workingGameConfig },
    isPrefixed ? path : `gameConfig.${gcPath}`,
    value
  );
  if (patch) {
    pushPreviewPatch(patch);
    return;
  }
  sceneEditorApplyLivePreview(workingGameConfig, selectedBase);
}

export function sceneEditorSchedulePersist({ key, path, value, setValue }) {
  persistLatest.set(key, { path, value, setValue });
  if (persistTimers.has(key)) clearTimeout(persistTimers.get(key));
  persistTimers.set(
    key,
    setTimeout(() => {
      persistTimers.delete(key);
      const pending = persistLatest.get(key);
      if (!pending) return;
      persistLatest.delete(key);
      pending.setValue({ path: pending.path, value: pending.value });
    }, PERSIST_DEBOUNCE_MS)
  );
}

/**
 * Flush debounced persist for one field. Call on gesture end (pointer-up).
 * Clears the working preview clone only when nothing else is pending,
 * so overlapping field keys cannot wipe an in-flight scrub snapshot.
 */
export function sceneEditorFlushPersist(key) {
  if (persistTimers.has(key)) {
    clearTimeout(persistTimers.get(key));
    persistTimers.delete(key);
  }
  const pending = persistLatest.get(key);
  persistLatest.delete(key);
  if (pending) {
    pending.setValue({ path: pending.path, value: pending.value });
  }
  if (persistTimers.size === 0 && persistLatest.size === 0) {
    workingGameConfig = null;
    workingVersionId = null;
  }
}

/**
 * Scene-editor number/slider write: gesture → live preview + debounced persist;
 * plain commit → immediate setValue (typing, blur, etc.).
 *
 * @param {object} args
 * @param {string} args.path - logical write path (pre-rewrite); passed to setValue
 * @param {*} args.value
 * @param {{ gesture?: boolean, gestureEnd?: boolean }} [args.options]
 * @param {Function} args.setValue - inspector setter (may rewrite localization paths)
 * @param {object} args.selectedVersion - real version (not effective-merged)
 * @param {object} args.selectedBase
 * @param {string} [args.selectedLanguage]
 * @param {boolean} [args.isLocalization]
 * @param {Function} [args.rewritePayload] - (payload, gameConfig, lang) => payload
 */
export function sceneEditorWriteNumberPath({
  path,
  value,
  options = {},
  setValue,
  selectedVersion,
  selectedBase,
  selectedLanguage,
  isLocalization = false,
  rewritePayload,
}) {
  const isGesture = Boolean(options.gesture) || Boolean(options.gestureEnd);

  if (!isGesture) {
    setValue({ path, value });
    return;
  }

  let previewPayload = { path, value };
  if (!isLocalization && typeof rewritePayload === "function") {
    previewPayload = rewritePayload(
      previewPayload,
      _.get(selectedVersion, "gameConfig"),
      selectedLanguage
    );
  }

  sceneEditorGesturePreviewSet({
    selectedVersion,
    selectedBase,
    path: previewPayload.path,
    value: previewPayload.value,
  });

  sceneEditorSchedulePersist({
    key: path,
    path,
    value,
    setValue,
  });

  if (options.gestureEnd) {
    sceneEditorFlushPersist(path);
  }
}
